import React, { useEffect, useState } from 'react';
import { Chip, CircularProgress, Link, List, ListItem, Button } from '@mui/material';
import { capitalize } from 'lodash-es';
import { makeStyles } from 'tss-react/mui';
import { useLocales } from '../../../hooks';
import {
  ContentCollectionBody,
  ContentCollectionResponse,
  CollectionLayoutsResponse,
  ContentCollectionType,
  HeroPresetCollectionResponse,
  HeroPresetCollectionApi
} from '../../../API';
import { useData } from '../../../data-layer';
import { pagePathToParam } from '../../../utils/layouts';
import { apiConfig } from '../../../utils/apiConfig';
import { LAYOUT_PATH_PREFIX } from '../../../utils/consts/uiModules';

const useStyles = makeStyles()((theme) => ({
  message: {
    marginTop: '20px',
    fontSize: '16px'
  },
  deleteGenreWarning: {
    fontSize: '14px',
    marginTop: '20px'
  },
  deleteWarningLabel: {
    marginRight: theme.spacing(1)
  },
  layoutsSection: {
    marginTop: theme.spacing(2)
  },
  layoutsList: {
    marginTop: theme.spacing(1),
    maxHeight: '300px',
    overflow: 'auto'
  },
  layoutItem: {
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center'
  },
  layoutPath: {
    marginRight: theme.spacing(1),
    fontFamily: 'monospace',
    fontSize: '14px'
  },
  layoutLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2)
  },
  toggleButton: {
    marginTop: theme.spacing(1),
    textTransform: 'none'
  }
}));

export const testIds = {
  componentRoot: 'collection-delete-confirm-component-root',
  layoutsList: 'collection-delete-confirm-layouts-list',
  layoutItem: (index: number): string => `collection-delete-confirm-layout-item-${index}`,
  loading: 'collection-delete-confirm-loading'
};

export function CollectionDeleteConfirm({
  collection,
  typeLabel,
  isHeroPreset = false
}: {
  collection: ContentCollectionResponse | ContentCollectionBody | HeroPresetCollectionResponse;
  typeLabel?: string;
  isHeroPreset?: boolean;
}): React.ReactElement {
  const { classes } = useStyles();
  const { t } = useLocales();
  const {
    collections: {
      hook: { getCollectionLayoutsById: getCollectionAssociatedLayouts }
    }
  } = useData();

  const [layouts, setLayouts] = useState<CollectionLayoutsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const INITIAL_DISPLAY_COUNT = 3;

  useEffect(() => {
    const fetchLayouts = async () => {
      try {
        setLoading(true);
        const entityId = (collection as ContentCollectionResponse).entityId;

        if (isHeroPreset) {
          const heroPresetAPI = new HeroPresetCollectionApi(apiConfig);
          const apiResponse = await heroPresetAPI.getAssociatedLayouts(entityId);
          setLayouts(apiResponse.data.body);
        } else {
          const response = await getCollectionAssociatedLayouts(entityId);
          setLayouts(response);
        }
      } catch (error) {
        console.error('Error fetching associated layouts:', error);
      } finally {
        setLoading(false);
      }
    };

    if ((collection as ContentCollectionResponse).entityId) {
      fetchLayouts();
    }
  }, [collection, getCollectionAssociatedLayouts, isHeroPreset]);

  const allLayouts = layouts ? [...layouts.live, ...layouts.drafts] : [];

  const getCollectionTypeName = () => {
    if (typeLabel) return typeLabel;

    if (isHeroPreset) {
      return t('collections.collection_type_names.hero_preset');
    }

    const contentCollection = collection as ContentCollectionResponse | ContentCollectionBody;
    if (contentCollection.collectionType === ContentCollectionType.TOP_10) {
      return t('collections.collection_type_names.top_10');
    }
    if (contentCollection.collectionType === ContentCollectionType.MIXED_LIST) {
      return t('collections.collection_type_names.mixed');
    }

    return t('collections.collection_type_names.vod');
  };

  const getLayoutUrl = (layout: (typeof allLayouts)[0]) => {
    const layoutType = layout.layoutType?.toUpperCase() || 'PAGE';
    const countries = layout.countries?.join('-') || '';
    const urlPathParam = pagePathToParam(layout.urlPath || '');
    return `${LAYOUT_PATH_PREFIX}${layoutType}/${countries}/${layout.id}/${urlPathParam}`;
  };

  const collectionName = getCollectionTypeName();

  return (
    <div data-testid={testIds.componentRoot}>
      <div className={classes.message}>{t('confirm.delete_collection_with_name', { name: collectionName })}</div>

      {!isHeroPreset && (collection as ContentCollectionResponse).genre?.key && (
        <div className={classes.deleteGenreWarning}>
          <Chip color="warning" label={t('filters.warning')} className={classes.deleteWarningLabel} size="small" />
          {t('confirm.delete_genre_collection', {
            genre: capitalize((collection as ContentCollectionResponse).genre?.key || '')
          })}
        </div>
      )}

      {loading && (
        <div className={classes.loadingContainer} data-testid={testIds.loading}>
          <CircularProgress size={24} />
        </div>
      )}

      {!loading && allLayouts.length > 0 && (
        <div className={classes.layoutsSection}>
          <div>{t('confirm.collection_associated_layouts', { name: collectionName })}</div>
          {allLayouts.length > INITIAL_DISPLAY_COUNT && (
            <Button variant="text" size="small" onClick={() => setExpanded(!expanded)} className={classes.toggleButton}>
              {expanded ? t('confirm.see_less') : t('confirm.see_more')}
            </Button>
          )}
          <List className={classes.layoutsList} data-testid={testIds.layoutsList}>
            {(expanded ? allLayouts : allLayouts.slice(0, INITIAL_DISPLAY_COUNT)).map((layout, index) => (
              <ListItem key={layout.id} className={classes.layoutItem} data-testid={testIds.layoutItem(index)}>
                <span className={classes.layoutPath}>{layout.urlPath}</span>
                <Link
                  href={getLayoutUrl(layout)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.layoutLink}
                >
                  {t('confirm.open')}
                </Link>
              </ListItem>
            ))}
          </List>
        </div>
      )}
    </div>
  );
}
